"use client";

import { useEffect } from "react";
import { computeVisualViewportState } from "@/lib/visual-viewport";

/** Delay (ms) para o iOS assentar o teclado antes de rolar o input à vista. */
const SCROLL_INTO_VIEW_DELAY_MS = 300;

const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

function isEditableTarget(target: EventTarget | null): target is HTMLElement {
  return target instanceof HTMLElement && target.matches(EDITABLE_SELECTOR);
}

/**
 * Ponte única entre a VisualViewport API e o CSS.
 * Monte uma vez perto da raiz. Não renderiza nada.
 */
export function VisualViewportBridge(): null {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const vv = window.visualViewport;
    const root = document.documentElement;
    let rafId: number | null = null;
    let scrollTimer: number | null = null;

    const writeVars = (): void => {
      const state = computeVisualViewportState({
        innerHeight: window.innerHeight,
        viewportHeight: vv ? vv.height : window.innerHeight,
        offsetTop: vv ? vv.offsetTop : 0,
      });
      root.style.setProperty("--vvh", `${state.height}px`);
      root.style.setProperty("--vv-offset-top", `${state.offsetTop}px`);
      root.style.setProperty("--vv-keyboard-inset", `${state.keyboardInset}px`);
      root.dataset.keyboardOpen = state.isKeyboardOpen ? "true" : "false";
    };

    const scheduleWrite = (): void => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        writeVars();
      });
    };

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const handleFocusIn = (event: FocusEvent): void => {
      if (!isEditableTarget(event.target)) {
        return;
      }
      if (!window.matchMedia("(pointer: coarse)").matches) {
        return;
      }
      const el = event.target;
      if (scrollTimer !== null) {
        window.clearTimeout(scrollTimer);
      }
      scrollTimer = window.setTimeout(() => {
        scrollTimer = null;
        el.scrollIntoView({
          block: "center",
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });
      }, SCROLL_INTO_VIEW_DELAY_MS);
    };

    // Sem `scroll`: o inset do teclado é propriedade de abrir/fechar (resize),
    // não de rolar — recalcular no scroll fazia o padding oscilar (offsetTop iOS).
    if (vv) {
      vv.addEventListener("resize", scheduleWrite);
    }
    window.addEventListener("resize", scheduleWrite);
    window.addEventListener("orientationchange", scheduleWrite);
    document.addEventListener("focusin", scheduleWrite);
    document.addEventListener("focusout", scheduleWrite);
    document.addEventListener("focusin", handleFocusIn);

    writeVars();

    return () => {
      if (vv) {
        vv.removeEventListener("resize", scheduleWrite);
      }
      window.removeEventListener("resize", scheduleWrite);
      window.removeEventListener("orientationchange", scheduleWrite);
      document.removeEventListener("focusin", scheduleWrite);
      document.removeEventListener("focusout", scheduleWrite);
      document.removeEventListener("focusin", handleFocusIn);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (scrollTimer !== null) {
        window.clearTimeout(scrollTimer);
      }
      root.style.removeProperty("--vvh");
      root.style.removeProperty("--vv-offset-top");
      root.style.removeProperty("--vv-keyboard-inset");
      delete root.dataset.keyboardOpen;
    };
  }, []);

  return null;
}
